import { AuthContext, AuthContextType } from "../../../context/AuthProvider";
import React, { ReactNode, useContext, useEffect, useState } from "react";
import { AxiosError } from "axios";
import { useQueryClient } from "@tanstack/react-query";
import CustomInput from "../Input/CustomInput";
import ResumeView from "../Output/ResumeView";
import ProfileImage from "../User/ProfileImage";
import GoAwayButton from "../Input/GoAwayButton";
import { ResumeDropzone } from "../Input/ResumeDropzone";
import { Tabs, TabContent } from "../Tabs/Tabs";
import ResumeQna, { QnaStyleEnum, QuestionData } from "../Input/ResumeQna";
import { useDrawer } from "./DrawerStateProvider";
//import { ResumeProfile } from "../../../DataModels/DataModels";
import { useResume } from "../../../services/api-client/useResume";
import { useModalState } from "../Modal/ModalStateProvider";
import OutlinedLabel from "../Output/OutlinedLabel";
import SubscriptionPanel, {
	SubscriptionContext,
	SubscriptionPanelMode
} from "../Output/SubscriptionPanel";
import useStripe from "../../../services/api-client/useStripe";
import useClaims from "../../../services/api-client/useClaims";
import useCancelSubscription from "../../../services/api-client/useCancelSubscription";
import {
	useSnackbar,
	LONG_TIMEOUT_IN_MS,
	MEDIUM_TIMEOUT_IN_MS
} from "../../../services/api-client/useSnackbar";
import { useCancelWizard } from "../Modal/CancelWizard/useCancelWizard";
import {
	cancellationSteps,
	defaultCancellationStep
} from "../Modal/CancelWizard/cancellationSteps";
import { useUserSubscriptionStats } from "../../../services/api-client/useUserSubscriptionStats";
import {
	useMyReferrals,
	useCreditBalance,
	useRevokeReferral,
	useCreateReferral
} from "../../../services/api-client/useReferrals";
import ActiveShareList from "../Output/ActiveShareList";
import CustomButton from "../Input/CustomButton";
import { CoffeeCup } from "../Icons/Interface";

const Account: React.FC = () => {
	const { hideTray } = useDrawer();
	const { resume, isLoading: resumeLoading } = useResume();
	const { openModal } = useModalState();

	const { user } = useContext(AuthContext) as AuthContextType;
	const queryClient = useQueryClient();
	const [userResume, setUserResume] = useState(resume ?? "");
	const email = user?.email || "";
	const name = user?.displayName || "";

	// Subscription hooks
	const userClaims = useClaims();
	const { customerPortalUrl } = useStripe();
	const { cancelSubscription, cancellationPending } = useCancelSubscription();
	const { showSnack } = useSnackbar();
	const { startCancelWizard } = useCancelWizard();
	const {
		subscriptionStats,
		isLoading: subscriptionStatsLoading,
		isError: subscriptionStatsError
	} = useUserSubscriptionStats();

	const ACTIVE_STATUSES = ["active", "past_due", "trialing"];
	const isPaid = ACTIVE_STATUSES.includes(userClaims?.subscription as string);

	const subscriptionContext: SubscriptionContext = {
		name,
		email,
		isPaid,
		planName: subscriptionStats?.planName ?? "Plan",
		customerPortalUrl: customerPortalUrl?.toString()
	};

	const getSubscriptionPanelMode = (): SubscriptionPanelMode => {
		if (!isPaid) return "normal";
		else if (subscriptionStatsLoading) return "loading";
		else if (subscriptionStatsError) return "error";
		else if (cancellationPending) return "busy";
		else return "normal";
	};

	useEffect(() => {
		if (subscriptionStatsError) {
			showSnack("NotifyNetworkError", "dark", MEDIUM_TIMEOUT_IN_MS);
		}
		// Disable ESLint here to prevent an infinite loop if showSnack is included as a dependency
		// eslint-disable-next-line
	}, [subscriptionStatsError]);

	const handleCancel = () => {
		// Do nothing if data is not available
		if (!subscriptionStats) {
			return;
		}

		const sub = subscriptionStats;

		const features = [
			{
				name: "AI Cover Letters",
				icon: "\u{1F4DD}",
				usageCount: sub.coverLettersGenerated
			},
			{
				name: "AI Resumes",
				icon: "\u{1F4C4}",
				usageCount: sub.resumesGenerated
			},
			{ name: "Job Tracking", icon: "\u{1F4CB}", usageCount: sub.jobsAdded }
		];

		// Fallback date is auth_time claim if sub.startedAt isn't present
		// This is not "correct", but is not needed in 99% of cases since only free
		// and exempt users lack startedAt, and the cancel wizard doesn't apply to free users
		const authTime = userClaims?.auth_time;
		const startedAtFallback = new Date(
			authTime ? parseInt(authTime) * 1000 : Date.now()
		).toISOString();

		const wizardData = {
			currentPrice: (sub.planRate ?? 0) / 100,
			newPrice: (sub.planDefaultRate ?? 0) / 100,
			memberSince: sub.startedAt ?? startedAtFallback,
			planName: subscriptionContext.planName,
			jobCount:
				features.find((f) => f.name === "Job Tracking")?.usageCount ?? 0,
			featureCount: features.length,
			features,
			// TODO: Wire up teamMemberCount and team.hasMembers from a team/org API
			//       if multi-seat subscriptions are supported.
			teamMemberCount: 0,
			team: { hasMembers: false }
		};

		startCancelWizard(
			defaultCancellationStep,
			cancellationSteps,
			wizardData,
			async () => {
				try {
					await cancelSubscription();
					showSnack("NotifySubscriptionCancelled", "dark", LONG_TIMEOUT_IN_MS);
				} catch (error) {
					if (error instanceof AxiosError && error.response?.status === 404) {
						await queryClient.invalidateQueries({
							queryKey: ["user", user?.uid, "claims"]
						});
					}
					console.error("Cancel subscription error:", error);
					showSnack(
						"NotifySubscriptionCancelError",
						"dark",
						LONG_TIMEOUT_IN_MS
					);
				}
			},
			() => {
				showSnack("NotifyPlanKept", "dark", MEDIUM_TIMEOUT_IN_MS);
			}
		);
	};
	const [phone, setPhone] = useState("");
	const [zipCode, setZipCode] = useState("");
	const [portfolioUrl, setPortfolioUrl] = useState("https://");
	const [portfolioPassword, setPortfolioPassword] = useState("");
	const [linkedinUrl, setLinkedinUrl] = useState("https://www.linkedin.com/");

	//console.log(`Resume:[${userResume}]`)
	//console.log(userResume)
	useEffect(() => {
		setUserResume(resume ?? "");
	}, [resumeLoading, resume]);

	// Validators
	const validateZipCode = (value: string) => {
		const zipRegex = /^\d{5}(-\d{4})?$/;
		return zipRegex.test(value) || value === "";
	};

	const validatePhone = (value: string) => {
		// Return true for empty strings if that's acceptable in your use case
		if (value === "") return true;

		// First, normalize the phone number by removing all non-digit characters
		// except for the plus sign at the beginning
		const normalizedValue = value.replace(/(?!^\+)\D/g, "");

		// Check for US/North American numbers (with or without country code)
		// Format: +1XXXXXXXXXX or 1XXXXXXXXXX or XXXXXXXXXX where X is 10 digits
		const usRegex = /^(\+?1)?[2-9]\d{2}[2-9]\d{2}\d{4}$/;

		// Check for international numbers (E.164 standard)
		// Generally: +[country code][area code][local number]
		// Must have at least 7 digits after country code, max 15 digits total
		// const internationalRegex = /^\+[1-9]\d{1,3}[1-9]\d{6,13}$/;

		// Check that it matches either US format or international format
		// For US/CA numbers, we ensure area code doesn't start with 0 or 1
		// and exchange code doesn't start with 0 or 1
		//return usRegex.test(normalizedValue) || internationalRegex.test(normalizedValue);
		return usRegex.test(normalizedValue);
	};

	const validateUrl = (value: string) => {
		// This regex checks for common URL patterns including:
		// - Required protocol (http:// or https://)
		// - Domain name with at least one dot
		// - Optional path, query parameters, and fragment
		// - Supports subdomains and various TLDs
		// - Allows common URL characters
		const urlRegex =
			/^(https?:\/\/)(www\.)?[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)+(\/[a-zA-Z0-9-._~:/?#[\]@!$&'()*+,;=]*)?$/;

		// Return true if it matches the pattern or if empty string
		return urlRegex.test(value) || value === "";
	};

	const validateLinkedinUrl = (value: string) => {
		return value.startsWith("https://www.linkedin.com/");
	};

	//Formatters
	const formatPhone = (value: string) => {
		// Remove all non-digit characters except the plus sign at the beginning
		const cleaned = value.replace(/(?!^\+)\D/g, "");

		// Handle empty input case
		if (cleaned.length === 0) return "";

		// Check if the number starts with a plus sign (international format)
		const isInternational = cleaned.startsWith("+");

		// For international numbers
		if (isInternational) {
			// Limit to reasonable length (15 is the maximum length per E.164 standard)
			const limitedDigits = cleaned.substring(0, 15);

			// Format based on country code length (most common are +1, +44, +86, etc.)
			//TODO: International Phone number formatting is poor.
			const countryCodeEnd = Math.min(3, limitedDigits.length - 1);
			const countryCode = limitedDigits.substring(0, countryCodeEnd + 1);
			const remaining = limitedDigits.substring(countryCodeEnd + 1);

			// Group the remaining digits in a readable format
			if (remaining.length > 0) {
				// Common grouping is 3-3-4 for many countries
				const groups = [];
				let i = 0;
				while (i < remaining.length) {
					const groupSize = i === 0 ? 3 : i === 3 ? 3 : 4;
					groups.push(remaining.substring(i, i + groupSize));
					i += groupSize;
					if (i >= 10) break; // Prevent excessive groups
				}
				return `${countryCode} ${groups.join("-")}`;
			}

			return countryCode;
		}
		// For domestic (US/North American) numbers
		else {
			// Limit to 10 digits for domestic format
			const limitedDigits = cleaned.substring(0, 10);

			// Format as (XXX) XXX-XXXX if we have enough digits
			if (limitedDigits.length === 10) {
				return `(${limitedDigits.substring(0, 3)}) ${limitedDigits.substring(3, 6)}-${limitedDigits.substring(6)}`;
			}
			// Format partially as user types
			else if (limitedDigits.length > 6) {
				return `(${limitedDigits.substring(0, 3)}) ${limitedDigits.substring(3, 6)}-${limitedDigits.substring(6)}`;
			} else if (limitedDigits.length > 3) {
				return `(${limitedDigits.substring(0, 3)}) ${limitedDigits.substring(3)}`;
			}

			return limitedDigits;
		}
	};

	const formatZipCode = (value: string) => {
		// Remove all non-digit characters
		const digits = value.replace(/\D/g, "");

		// Handle empty input case
		if (digits.length === 0) return "";

		// Limit to maximum 9 digits
		const limitedDigits = digits.substring(0, 9);

		// Format as ZIP+4 if more than 5 digits
		if (limitedDigits.length > 5) {
			return `${limitedDigits.substring(0, 5)}-${limitedDigits.substring(5)}`;
		}

		// Otherwise just return the first 5 digits
		return limitedDigits;
	};

	//Ux Controls
	const tabAccount = (): ReactNode => {
		return (
			<>
				{/* Name - Read Only */}
				<OutlinedLabel label="Name" value={name} />

				{/* Email - Read Only */}
				<OutlinedLabel label="Account Email" value={email} />

				{/* Subscription Panel */}
				<div className="pb-6">
					<SubscriptionPanel
						user={subscriptionContext}
						onUpgrade={() => openModal("UpgradeSub")}
						onManage={() =>
							window.open(customerPortalUrl?.toString(), "_blank")
						}
						onCancel={handleCancel}
						mode={getSubscriptionPanelMode()}
					/>
				</div>
			</>
		);
	};

	const tabResume = (): ReactNode => {
		return (
			<>
				<p className="ml-4 text-primary-active">Account Docs</p>
				<ResumeDropzone
					onSuccess={(data) => {
						setUserResume(data);
					}}
					onError={(err) => {
						console.log(err);
					}}
				/>
				{/* {console.log(userResume)} */}
				<ResumeView
					tailClassMaxHeight="max-h-[calc(100vh-420px)]"
					resume={resumeLoading ? "Loading..." : userResume}
				/>
			</>
		);
	};

	// Referral hooks
	const { referrals, isLoading: referralsLoading } = useMyReferrals();
	const { balance: creditBalance, pendingPhases } = useCreditBalance();
	const pendingCredits = pendingPhases.reduce((sum, p) => sum + p.credits, 0);
	const revokeReferral = useRevokeReferral();
	const createReferral = useCreateReferral();

	const groupShareEnabled =
		Array.isArray(userClaims?.perms) &&
		(userClaims?.perms as string[]).includes("features/group-share");

	const tabShareJobMap = (): ReactNode => {
		const handleShareFriend = () => {
			openModal("ShareWizard", {
				mode: "friend" as const,
				onCreateReferral: (data: any) => createReferral.mutateAsync(data)
			});
		};

		const handleShareGroup = () => {
			openModal("ShareWizard", {
				mode: "group" as const,
				onCreateReferral: (data: any) => createReferral.mutateAsync(data)
			});
		};

		const handleOpenCreditStore = () => {
			openModal("CreditStore", { userBalance: creditBalance });
		};

		const handleRevoke = (code: string) => {
			revokeReferral.mutate(code);
		};

		const copyShareLink = (code: string) => {
			const link = `${window.location.origin}/r/${code}`;
			navigator.clipboard.writeText(link);
			showSnack("NotifyLinkCopied", "dark", MEDIUM_TIMEOUT_IN_MS);
		};

		return (
			<div className="flex flex-col" style={{ height: "calc(100vh - 220px)" }}>
				{/* Credit Balance */}
				<OutlinedLabel
					label="Credit Balance"
					value={`${creditBalance} credits earned · ${pendingCredits} pending`}
				/>

				{/* Invite & Earn Credits Header */}
				<div className="mt-4 mb-3">
					<p className="text-primary-active font-bold text-sm">
						Invite &amp; Earn Credits
					</p>
				</div>

				{/* Share with a Friend - CustomButton */}
				<div className="mb-3">
					<CustomButton
						title="One-to-One Invite"
						text="Share with a Friend"
						rightIcon="External"
						onClick={handleShareFriend}
					/>
				</div>

				{/* Share with a Group - CustomButton (gated) */}
				{groupShareEnabled && (
					<div className="mb-3">
						<CustomButton
							title="One-to-Many Code"
							text="Share with a Group"
							rightIcon="External"
							onClick={handleShareGroup}
						/>
					</div>
				)}

				{/* Horizontal Divider */}
				<hr className="border-primary-accent my-3" />

				{/* Active Shares — fills remaining space with scrollable overflow */}
				<div className="flex-1 min-h-0 overflow-y-auto">
					<ActiveShareList
						referrals={referrals.filter((r) => r.status !== "revoked")}
						isLoading={referralsLoading}
						onCopyLink={copyShareLink}
						onRevoke={handleRevoke}
					/>
				</div>

				{/* Credit Store Section (pinned to bottom) */}
				<div className="flex-shrink-0 pt-4">
					<hr className="border-primary-accent mb-3" />
					<p className="text-primary-active font-bold text-sm mb-3">
						Credit Store
					</p>
					<CustomButton
						title={`${creditBalance} credits available to spend`}
						text="Open Credit Store"
						leftIcon={CoffeeCup()}
						rightIcon="External"
						onClick={handleOpenCreditStore}
					/>
				</div>
			</div>
		);
	};

	// eslint-disable-next-line
	const tabPreferance = (): ReactNode => {
		return (
			<>
				{/* Phone Input */}
				<CustomInput
					label="Phone Number"
					initialValue={phone}
					onChange={setPhone}
					onValidate={validatePhone}
					formatter={formatPhone}
					validationText="Please use domestic format (555-555-5555)"
				/>

				{/* Linkedin Input */}
				<CustomInput
					label="Zip Code"
					initialValue={zipCode}
					onChange={setZipCode}
					onValidate={validateZipCode}
					formatter={formatZipCode}
					validationText="Please enter a valid zip code (e.g., 12345 or 12345-6789)"
				/>

				{/* Porfolio Input */}
				<CustomInput
					label="Website / Portfolio"
					initialValue={portfolioUrl}
					onChange={setPortfolioUrl}
					onValidate={validateUrl}
					validationText="Please enter a valid web address"
				/>

				{/* Porfolio Password */}
				<CustomInput
					label="Website Password"
					initialValue={portfolioPassword}
					onChange={setPortfolioPassword}
				/>

				{/* Linkedin Input */}
				<CustomInput
					label="Linkedin"
					initialValue={linkedinUrl}
					onChange={setLinkedinUrl}
					onValidate={(s: string) => {
						return validateUrl(s) && validateLinkedinUrl(s);
					}}
					validationText="Please enter a valid Linkedin address"
				/>
			</>
		);
	};

	async function AudioRecived(audioBlob: Blob) {
		console.log(`AudioRecived type: ${audioBlob.type} Size:${audioBlob.size}`);

		// Create a URL for the blob
		const audioUrl = URL.createObjectURL(audioBlob);

		// Create an audio element
		const audioElement = new Audio();

		// Set the source to the blob URL
		audioElement.src = audioUrl;

		// Optional: Add event listeners for different audio states
		audioElement.onloadedmetadata = () => {
			console.log(`Audio duration: ${audioElement.duration} seconds`);
		};

		audioElement.onended = () => {
			console.log("Audio playback finished");
			// Clean up the blob URL when done to free memory
			URL.revokeObjectURL(audioUrl);
		};

		// Start playing
		audioElement
			.play()
			.then(() => console.log("Audio playback started"))
			.catch((error) => console.error("Error playing audio:", error));

		return "Sample TTS";
	}

	// Sample resume improvement questions
	const resumeQuestions: QuestionData[] = [
		{
			id: 1,
			question:
				"Have you worked for a FAANG company, and describe a time where you demonstrated specific leadership skills?",
			answer: ""
		},
		{
			id: 2,
			question: "What is your highest level of education?",
			answer: ""
		},
		{
			id: 3,
			question: "How many years of experience do you have?",
			answer: ""
		},
		{
			id: 4,
			question: "Do you have leadership experience?",
			answer: ""
		},
		{
			id: 5,
			question: "What industry are you targeting?",
			answer: ""
		}
	];

	// eslint-disable-next-line
	const tabQna = (): ReactNode => {
		return (
			<>
				<ResumeQna
					questions={resumeQuestions}
					speechToText={AudioRecived}
					displayAs={QnaStyleEnum.Stack}
				/>
			</>
		);
	};

	return (
		<React.StrictMode>
			<div className="w-[500px]">
				<div
					className="relative pt-4 px-6 pb-16 bg-cover bg-center"
					style={{ backgroundImage: "url(./headerContour.png)" }}
				>
					<div className="flex flex-row justify-between items-start">
						<ProfileImage />
						<GoAwayButton
							className="text-primary stroke-primary fill-primary hover:stroke-primary-accent hover:fill-primary-accent"
							onClick={hideTray}
						/>
					</div>

					<div className="m-auto"></div>
				</div>

				<Tabs className="text-sm bg-primary-background px-6">
					{[
						<TabContent
							key="resume"
							title="Resume"
							className="flex flex-col space-y-4 px-6"
						>
							{tabResume()}
						</TabContent>,
						...(isPaid
							? [
									<TabContent
										key="share"
										title="Share JobMap"
										className="flex flex-col space-y-4 px-6"
									>
										{tabShareJobMap()}
									</TabContent>
								]
							: []),
						<TabContent
							key="account"
							title="Account"
							className="flex flex-col space-y-4 px-6"
						>
							{tabAccount()}
						</TabContent>
					]}
				</Tabs>
			</div>
		</React.StrictMode>
	);
};

export default Account;
